import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { areas as areasApi, auth } from "../api/endpoints";
import {
  errorMessage,
  setUnauthorizedHandler,
  tokenStore,
  userStore,
} from "../lib/http";
import { BUSINESS_RULES, ROL_ADMIN } from "../lib/constants";
import { anotarSesion } from "../lib/monitoreo";

/* =========================================================================
   SESIÓN Y PERMISOS
   El backend devuelve { user, token } en /login. El panel solo admite rol 4:
   dejar entrar a un tendero a la administración sería una escalada de
   privilegios, así que se rechaza en el cliente Y el servidor lo vuelve a
   validar en cada endpoint /admin (nunca se confía solo en el navegador).

   El rol 4 dice "es del equipo"; el ÁREA dice qué secciones le tocan. Los
   permisos se cargan al iniciar sesión y se guardan acá para que el menú y las
   rutas sepan qué mostrar. Ocultar una sección es comodidad de la interfaz: el
   middleware `modulo:` del servidor la rechaza igual si alguien escribe la URL
   a mano.
   ======================================================================== */

const AuthCtx = createContext(null);

/**
 * Reglas mientras la respuesta del servidor no ha llegado.
 *
 * Son las de fábrica del backend, no una segunda fuente de verdad: en cuanto
 * llega `/me/permissions` se reemplazan por las vigentes. Existen para que el
 * primer fotograma no muestre "comisión 0 %" ni "máximo 0 entregas", que se
 * leería como un dato real y equivocado.
 */
const REGLAS_POR_DEFECTO = {
  domiciliary_share: BUSINESS_RULES.domiciliaryShare,
  max_active_deliveries: BUSINESS_RULES.maxActiveDeliveries,
  stalled_hours: 24,
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => userStore.get());
  const [permisos, setPermisos] = useState(null);
  const [area, setArea] = useState(null);
  const [nivel, setNivel] = useState(null);
  const [reglas, setReglas] = useState(null);
  const [booting, setBooting] = useState(() => Boolean(tokenStore.get()));

  const clear = useCallback(() => {
    tokenStore.set(null);
    userStore.set(null);
    setUser(null);
    setPermisos(null);
    setArea(null);
    setNivel(null);
    anotarSesion(null);
  }, []);

  /**
   * Trae el área y los permisos de quien entró.
   *
   * Si falla se deja el mapa VACÍO en vez de suponer acceso total: ante la duda
   * el panel muestra de menos, nunca de más. Un menú incompleto se resuelve
   * recargando; uno que ofrece secciones prohibidas llena la pantalla de 403.
   */
  const cargarPermisos = useCallback(async () => {
    try {
      const datos = await areasApi.misPermisos();
      setPermisos(datos?.permissions ?? {});
      setArea(datos?.area ?? null);
      setNivel(datos?.access_level ?? null);
      /*
       * Las reglas VIGENTES de la operación llegan con los permisos.
       *
       * El panel las tenía copiadas en `lib/constants.js`, que servía mientras
       * vivían en el `.env` y no cambiaban nunca. Desde que se editan desde
       * Ajustes, esa copia se queda vieja en cuanto alguien las toca: la
       * pantalla diría "máximo 3 entregas" mientras el servidor ya permite
       * cinco. Vienen por acá porque es la única llamada que hace todo el
       * panel al entrar, sin importar el área.
       */
      setReglas(datos?.rules ?? null);

      /*
       * Al monitoreo va el identificador y el ÁREA, que es lo que explica por
       * qué esa persona veía esa pantalla. Ni correo ni nombre: para arreglar
       * un error hace falta el contexto, no la identidad.
       */
      anotarSesion({
        id: userStore.get()?.user_id,
        area: datos?.area?.code,
        nivel: datos?.access_level,
      });

      return datos;
    } catch {
      setPermisos({});
      setArea(null);
      setNivel(null);
      return null;
    }
  }, []);

  // Un 401 en cualquier petición cierra la sesión en toda la app.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clear();
    });
  }, [clear]);

  // Al recargar con token guardado se revalida contra /profile: el token
  // pudo revocarse desde otro dispositivo y no tiene sentido pintar el panel
  // entero para que después falle cada consulta.
  useEffect(() => {
    let vivo = true;

    // Sin token no hay nada que revalidar. No hace falta apagar `booting`:
    // arranca en `Boolean(tokenStore.get())`, así que ya está apagado — la
    // escritura que había acá no cambiaba nada y disparaba un render de más.
    if (!tokenStore.get()) return;
    auth
      .profile()
      .then(async (perfil) => {
        if (!vivo) return;
        const u = perfil?.user ?? perfil;
        if (Number(u?.rol) !== ROL_ADMIN) {
          clear();
          return;
        }
        userStore.set(u);
        setUser(u);
        // Los permisos no se guardan en localStorage: si a alguien le cambian
        // el área, una copia local haría que siguiera viendo el menú anterior
        // hasta cerrar sesión.
        await cargarPermisos();
      })
      .catch(() => {
        if (vivo) clear();
      })
      .finally(() => {
        if (vivo) setBooting(false);
      });
    return () => {
      vivo = false;
    };
  }, [clear, cargarPermisos]);

  const login = useCallback(
    async (email, password, codigo) => {
      try {
        const data = await auth.login(email, password, codigo);
        const u = data?.user;

        if (Number(u?.rol) !== ROL_ADMIN) {
          return {
            ok: false,
            message:
              "Esta cuenta no tiene permisos de administración. Ingresa con un usuario administrador.",
          };
        }

        tokenStore.set(data.token);
        userStore.set(u);
        setUser(u);

        const permisos = await cargarPermisos();

        // Entrar sin área es entrar a un panel vacío. Se dice acá, en el login,
        // en vez de dejar que la persona descubra un menú en blanco.
        if (permisos && !permisos.area) {
          // Se deshace la sesión: dejar el token guardado con el login rechazado
          // haría que la siguiente recarga entrara a un panel sin menú.
          clear();
          return {
            ok: false,
            message:
              "Tu cuenta no tiene un área asignada. Pídele a Tecnología que te asigne una para poder entrar.",
          };
        }

        return { ok: true };
      } catch (e) {
        /*
         * El servidor pide el segundo factor con `reason`, y la pantalla lo
         * necesita para saber si tiene que enseñar el campo del código o si es
         * un error de credenciales corriente. Sin distinguirlo, la persona
         * vería "no se pudo iniciar sesión" con la clave bien puesta.
         */
        const motivo = e?.response?.data?.reason ?? null;

        return {
          ok: false,
          motivo,
          message: errorMessage(e, "No se pudo iniciar sesión."),
        };
      }
    },
    [cargarPermisos],
  );

  const logout = useCallback(async () => {
    // Se invalida el token en el servidor, pero la sesión local se limpia
    // pase lo que pase: si la red falla, el usuario igual queda fuera.
    try {
      await auth.logout();
    } catch {
      /* sin red o token ya vencido */
    } finally {
      clear();
    }
  }, [clear]);

  const value = useMemo(() => {
    /*
     * Mientras los permisos no han llegado (`null`) se responde que NO a todo.
     * Responder que sí "por si acaso" haría parpadear el menú completo antes de
     * recortarse, que es peor: se alcanza a ver lo que no corresponde.
     */
    const puedeVer = (modulo) => Boolean(permisos?.[modulo]?.view);
    const puedeGestionar = (modulo) => Boolean(permisos?.[modulo]?.manage);

    return {
      user,
      area,
      /*
       * 'gestor' o 'consulta'. Los permisos ya llegan recortados por el
       * servidor, así que la interfaz no tiene que aplicar el nivel: solo lo
       * usa para DECIRLO, porque quien solo consulta no ve botones de crear y
       * sin el rótulo parecería que el panel se le rompió.
       */
      nivel,
      permisos,
      /*
       * Reglas vigentes de la operación, tal como las aplica el servidor. Con
       * los valores por defecto del catálogo mientras no han llegado: pintar
       * ceros haría creer que la comisión es cero.
       */
      reglas: reglas ?? REGLAS_POR_DEFECTO,
      puedeVer,
      puedeGestionar,
      recargarPermisos: cargarPermisos,
      booting,
      login,
      logout,
      isAuthenticated: Boolean(user),
    };
  }, [
    user,
    area,
    nivel,
    reglas,
    permisos,
    booting,
    login,
    logout,
    cargarPermisos,
  ]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth necesita estar dentro de <AuthProvider>");
  return ctx;
};
